<template>
  <div class="clinic-dashboard">
    <PageHeader title="داشبورد سامانه مطب" subtitle="دسترسی سریع" :infoLearnUrl="''" infoTitle="راهنمای داشبورد">
      <template #actions>
        <LottieButton
          src="/img/refresh.json"
          :size="36"
          :clickToPlay="true"
          :buttonClass="'btn btn-ghost btn-refresh'"
          caption="به روزرسانی"
          aria-label="بارگذاری مجدد"
          @activated="refreshData"
        />
      </template>
      <template #info>
        <p>
          در قسمت داشبورد امکان مشاهده خلاصه وضعیت سامانه وجود دارد.
        </p>
        <p>
          برای دسترسی به هر کدام از زیربرنامه ها کافیست از قسمت دسترسی سریع روی آیتم مد نظر کلیک نمایید.
        </p>
      </template>
    </PageHeader>

  <!-- Stat cards removed per user request: patientsCount, upcomingAppointments, monthRevenue -->

    <section class="quick-links">
      <ClinicQuickLinks showOnDesktop :colorful="true" />
    </section>

    <!-- Clinic status summary title -->
    <section class="clinic-summary">
      <h3 class="clinic-summary__title">خلاصه وضعیت سامانه مطب</h3>
      <div class="summary-grid">
        <div class="summary-col">
          <div class="summary-group">
            <h4 class="summary-group__title">نوبت دهی بیماران</h4>
            <MetricGrid class="summary-cards">
              <MetricCard label="تعداد بیماران امروز" :value="toPersianDigits(String(todayCount || 0))" />
              <MetricCard label="اولین نوبت" :value="firstAppointment" />
              <MetricCard label="آخرین نوبت" :value="lastAppointment" />
              <MetricCard label="بیماران ویزیت شده تا کنون" :value="toPersianDigits(String(visitedCount || 0))" />
              <MetricCard label="وضعیت اتاق انتظار (حاضرین)" :value="toPersianDigits(String(waitingCount || 0))" />
            </MetricGrid>
          </div>

          <div class="summary-group">
            <h4 class="summary-group__title">بیماران </h4>
            <div class="new-patients-list">
              <div class="new-patients-item">تعداد بیماران ثبت شده: <strong>{{ toPersianDigits(String(totalPatientsCount || 0)) }}</strong></div>
              <div class="new-patients-item">امروز: <strong>{{ toPersianDigits(String(newPatientsToday || 0)) }}</strong></div>
              <div class="new-patients-item">هفته جاری: <strong>{{ toPersianDigits(String(newPatientsWeek || 0)) }}</strong></div>
              <div class="new-patients-item">این ماه: <strong>{{ toPersianDigits(String(newPatientsMonth || 0)) }}</strong></div>
            </div>
          </div>
        </div>

        <div class="summary-col">
          <div class="summary-group">
            <h4 class="summary-group__title">وضعیت مالی</h4>
            <div class="summary-cards financial">
              <div class="metrics-col">
                <MetricGrid>
                  <MetricCard label="درآمد این ماه" :value="formatCurrency(monthRevenue)" variant="financial" />
                  <MetricCard label="مبلغ دریافتی امروز" :value="formatCurrency(amountReceivedToday)" variant="financial" />
                  <MetricCard label="تعداد قبض‌های صادر شده" :value="toPersianDigits(String(invoicesToday || 0))" variant="financial" />
                </MetricGrid>
              </div>
              <div class="chart-col">
                <div class="chart-card">
                  <div class="chart-title">نمودار درآمد</div>
                  <div class="chart-wrapper">
                    <svg viewBox="0 0 36 36" class="summary-pie" role="img" aria-label="نمودار درآمد ماه و دریافتی امروز" preserveAspectRatio="xMidYMid meet">
                      <title>نمودار درآمد ماه و دریافتی امروز</title>
                      <desc>آبی: سهم درآمد ماهیانه، سبز: دریافتی امروز</desc>
                      <circle class="circle-bg" cx="18" cy="18" r="15.9155" fill="none" stroke-width="2" stroke="#eee"></circle>
                      <circle class="circle-month" cx="18" cy="18" r="15.9155" fill="none" stroke-width="2"
                        :stroke-dasharray="piePercentMonth + ', 100'" stroke="#4f46e5" transform="rotate(-90 18 18)"/>
                      <circle class="circle-today" cx="18" cy="18" r="15.9155" fill="none" stroke-width="2"
                        :stroke-dasharray="piePercentToday + ', 100'" stroke="#10b981" transform="rotate(-90 18 18)" stroke-linecap="butt"/>
                      <text x="18" y="19" class="pie-label">{{ formatCurrency(monthRevenue) }}</text>
                    </svg>
                  </div>
                </div>
              </div>
            
            </div>
          </div>

          <div class="summary-group">
            <h4 class="summary-group__title">گزارش قبوض ماهانه</h4>
            <div class="bills-summary">
              <div class="bills-content">
                <div class="info-value">{{ formatCurrency(monthlyBills.total) }}</div>
                <div class="info-sub">تعداد: {{ toPersianDigits(monthlyBills.count) }}</div>
                <div class="info-sub">از ابتدای ماه: <strong>{{ formatJalaliDate(monthStart) }}</strong></div>
                <div class="info-actions">
                  <button class="btn btn-primary" @click="openMonthlyBills">مشاهده گزارش</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- appointments-panel removed as per request (نوبت‌های امروز و لیست حذف شد) -->
  </div>
</template>

<script setup>
import { onMounted, computed, ref } from 'vue';
import { useRouter } from 'vue-router';
// router not needed in this view; quick links component handles navigation
import ClinicQuickLinks from '@/components/ClinicQuickLinks.vue';
import LottieButton from '@/components/LottieButton.vue';
import PageHeader from '@/components/PageHeader.vue';
import MetricGrid from '@/components/MetricGrid.vue';
import MetricCard from '@/components/MetricCard.vue';
import { toPersianDigits, formatCurrency, formatJalaliDate } from '@/utils/formatters.js';

import { useDailyVisitsStore } from '@/stores/clinic/dailyVisits.js';
import { usePatientsStore } from '@/stores/clinic/patients.js';
import moment from 'jalali-moment';
// small helper for currency formatting (copy from user-area)
// Currency helpers removed; not used in this view after UI simplification

// publicPath not used here; quick links component handles its own paths

// no longer using patientsCount, upcomingAppointments, monthRevenue, upcomingAppointmentsList on this dashboard

// Quick links are now provided by ClinicQuickLinks component which handles its own data and navigation

// quick store wiring for summary section
const dailyVisitsStore = useDailyVisitsStore();
const patientsStore = usePatientsStore();

// today ref not used for now

async function refreshData() {
  // Fetch data from stores (best-effort) and compute metrics
  try {
    await patientsStore.fetchPatients(1);
    // dailyVisitsStore.filteredReceptions reads from receptionsStore, no fetch needed
  } catch (err) {
    // ignore errors for now
  }
}

// Lottie handling moved to a reusable component LottieButton.vue

// use centralized formatters.js to convert numbers and dates

// helper check for cancelled reception fields - trying several possible fields
function isCancelled(reception) {
  if (!reception) return false;
  if (reception.fRcancel || reception.cancelled || reception.isCanceled) return true;
  if (reception.fRstatus && String(reception.fRstatus).toLowerCase().includes('لغو')) return true;
  if (reception.status && String(reception.status).toLowerCase().includes('cancel')) return true;
  return false;
}

// helper to determine if reception is visited (approximation: if time is in past)
function isVisited(reception) {
  if (!reception) return false;
  if (reception.fRvisit === true || reception.visited === true) return true;
  const time = reception.fRtime || reception.fRtim || reception.time || reception.fRtime;
  if (!time) return false;
  // time format HH:mm — compare with now (local)
  try {
    const now = moment();
    const check = moment(time, 'HH:mm');
    if (!check.isValid()) return false;
    // rush if appointment's hour is before now
    if (check.hours() < now.hours() || (check.hours() === now.hours() && check.minutes() <= now.minutes())) return true;
  } catch (e) {
    return false;
  }
  return false;
}

// computed metrics (appointments)
const todayCount = computed(() => {
  const list = dailyVisitsStore.filteredReceptions || [];
  return (list || []).length;
});
const firstAppointment = computed(() => {
  const list = dailyVisitsStore.filteredReceptions || [];
  if (!list || list.length === 0) return '-';
  const times = list.map(r => r.fRtime || r.time || '');
  const sorted = times.filter(Boolean).sort((a, b) => a.localeCompare(b));
  return sorted[0] || '-';
});
const lastAppointment = computed(() => {
  const list = dailyVisitsStore.filteredReceptions || [];
  if (!list || list.length === 0) return '-';
  const times = list.map(r => r.fRtime || r.time || '');
  const sorted = times.filter(Boolean).sort((a, b) => a.localeCompare(b));
  return sorted[sorted.length - 1] || '-';
});
const visitedCount = computed(() => {
  const list = dailyVisitsStore.filteredReceptions || [];
  return (list || []).filter(isVisited).length;
});
const waitingCount = computed(() => {
  const list = dailyVisitsStore.filteredReceptions || [];
  return (list || []).filter(r => !isVisited(r) && !isCancelled(r)).length;
});

// new patients counters (best-effort using patientsStore if it includes created date)
const totalPatientsCount = computed(() => {
  return patientsStore.pagination?.total || patientsStore.patientsList.length || 0;
});
const newPatientsToday = computed(() => {
  const list = patientsStore.patientsList || [];
  const todayStr = moment().format('YYYY-MM-DD');
  return (list || []).filter(p => p.createdAt && p.createdAt.startsWith(todayStr)).length;
});
const newPatientsWeek = computed(() => {
  const list = patientsStore.patientsList || [];
  const startOfWeek = moment().startOf('week');
  return (list || []).filter(p => p.createdAt && moment(p.createdAt).isSameOrAfter(startOfWeek)).length;
});
const newPatientsMonth = computed(() => {
  const list = patientsStore.patientsList || [];
  const startOfMonth = moment().startOf('month');
  return (list || []).filter(p => p.createdAt && moment(p.createdAt).isSameOrAfter(startOfMonth)).length;
});

// Financial metrics (placeholders, can be wired to real API later)
const monthRevenue = ref(0);
const amountReceivedToday = ref(0);
const invoicesToday = ref(0);

// Monthly bills data
const monthlyBills = ref({ count: 0, total: 0 });
const monthStart = ref('');

// health / alert metrics placeholders
const lowStockCount = ref(0);
const debtorsCount = ref(0);

const router = useRouter();

onMounted(async () => {
  await refreshData();
  // set demo values if the values are still zero — optional: replace with API calls later
  monthRevenue.value = monthRevenue.value || 2500000;
  amountReceivedToday.value = amountReceivedToday.value || 450000;
  invoicesToday.value = invoicesToday.value || 15;
  lowStockCount.value = lowStockCount.value || 3;
  debtorsCount.value = debtorsCount.value || 5;

  // Sample monthly bills data (could be fetched from API)
  monthStart.value = moment().startOf('jMonth').format('YYYY-MM-DD');
  monthlyBills.value = { count: 12, total: 1250000 };
});

// compute pie chart percents and currency formatting are defined later (single copy)

// formatCurrency is provided by utils/formatters.js

const piePercentToday = computed(() => {
  const m = Number(monthRevenue.value) || 0;
  const t = Number(amountReceivedToday.value) || 0;
  if (!m) return 0;
  const p = Math.min(100, Math.round((t / m) * 100));
  return p;
});
const piePercentMonth = computed(() => {
  // show full circle for month revenue (as baseline). We'll cap at 100
  const m = Number(monthRevenue.value) || 0;
  if (!m) return 0;
  return 100;
});

// Navigation handled inside ClinicQuickLinks

// goToPatient removed since the appointments section is removed

// Open monthly bills report
function openMonthlyBills() {
  // open reports with query params from start of month
  router.push({ path: '/overview/clinic/clinic-reports', query: { report: 'bills', from: monthStart.value } });
}

// Quick attempt to populate patients count from store is already done in onMounted above
</script>

<style scoped>
.clinic-dashboard {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
}
.header-row {
  display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:14px
}
.quick-links{margin-top:10px}
.quick-links h3{margin:8px 0;color:var(--text-color-primary);font-weight:600}
.links-grid{display:block}
.appointments-panel{margin-top:16px}
.appointments-list{display:flex;flex-direction:column;gap:8px}
.appt-item{display:flex;align-items:center;justify-content:space-between;background:var(--background-color-secondary);padding:10px;border-radius:8px}
.no-data{color:var(--muted-color);padding:12px}

.clinic-summary{margin-top:16px;padding:8px 6px}
.clinic-summary__title{font-size:16px;margin:6px 0;color:var(--text-color-primary);font-weight:700}
.summary-grid{display:flex;gap:12px;flex-wrap:wrap}
.summary-col{flex:1;min-width:300px}
  /* Pale box for each group using theme variables to match dark/light themes */
  .summary-group{background:var(--background-color-secondary);border:1px solid rgba(var(--text-color-secondary-rgb),0.08);padding:10px;border-radius:8px;margin-bottom:10px}
.summary-group__title{font-size:14px;color:var(--text-color-primary);margin-bottom:8px}
.summary-cards{display:flex;flex-wrap:wrap;gap:8px}
.summary-cards.financial{display:flex;gap:10px;align-items:stretch;direction:ltr;flex-wrap:nowrap}
.summary-cards.financial .chart-col{flex: 0 0 50%;max-width:50%;min-width:0;display:flex;align-items:center;justify-content:center}
.summary-cards.financial .metrics-col{flex: 0 0 50%;max-width:50%;display:flex;flex-direction:column;gap:8px;direction:rtl}
.summary-cards.financial .metric-card{min-width:0;width:100%}
.metric-card{display:flex;flex-direction:column;gap:6px;padding:10px;background:var(--background-color);border:1px solid rgba(var(--text-color-secondary-rgb),0.06);border-radius:6px;min-width:150px;align-items:flex-start;box-shadow: 0 0 0 1px rgba(0,0,0,0) inset}
.metric-label{color:var(--text-color-secondary);font-size:12px}
.metric-value{font-size:18px;color:var(--text-color-primary);font-weight:700}
.chart-card{min-width:180px}
.summary-cards.financial .chart-card{min-width:200px}
.chart-title{font-size:13px;color:var(--text-color-secondary);margin-bottom:6px}
.chart-wrapper{display:flex;justify-content:center;align-items:center}
.summary-pie{width:200px;height:200px}
.pie-label{font-size:3.5px;fill:var(--text-color-primary);text-anchor:middle}
/* Refresh button replacement styles */
.actions .btn-refresh{padding:6px;display:inline-flex;align-items:center;justify-content:center;min-width:44px;min-height:44px;border-radius:6px;background:transparent;border:none !important;box-shadow:none !important;border-color:transparent !important}
.actions .btn-refresh:hover{background:transparent !important;border:none !important;box-shadow:none !important}
.actions .btn-refresh lottie-player{display:block}
.actions .btn-refresh{cursor:pointer}
.actions .btn-refresh:focus{outline:none !important;box-shadow:none !important;border:none !important}
.actions .btn-refresh:focus-visible{outline:none !important}
.actions .btn-refresh[disabled]{pointer-events:none;opacity:0.7}
.alert-list{list-style:none;padding:0;margin:0;color:var(--text-color-secondary);display:flex;flex-direction:column;gap:6px}
.new-patients-list{display:flex;flex-direction:column;gap:8px}
.new-patients-item{color:var(--text-color-secondary)}

.cards-row{display:none}

/* Monthly Bills Styles */
.bills-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bills-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-value {
  font-weight: 800;
  color: var(--text-color-primary);
  font-size: 20px;
}

.info-sub {
  font-size: 13px;
  color: var(--text-color-secondary);
}

.info-actions {
  width: 100%;
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
}

.info-actions .btn {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  background-color: var(--background-color-button);
  color: white;
}

.info-actions .btn-primary:hover {
  background-color: var(--background-color-button-hover);
}

@media (max-width: 640px) {
  .summary-cards.financial{flex-direction:column;direction:rtl}
  .summary-cards.financial .chart-col, .summary-cards.financial .metrics-col{width:100%}
}

/* Note: Stat cards were removed; keep this rule in place while the UI is simplified. */
</style>
